import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import Img from '../components/ui/Img';
import { programmes } from '../data';
import { IcoCheck, IcoArrow, IcoChevronDown,
  IcoHome, IcoActivity, IcoShield, IcoUsers,
  IcoHeart, IcoBook, IcoLeaf, IcoAward, IcoTarget } from '../assets/icons';

const progIcons = {
  pdic:          IcoHome,
  tata:          IcoActivity,
  geracao:       IcoShield,
  'seja-mulher': IcoUsers,
  dikota:        IcoHeart,
  campus:        IcoBook,
  revieng:       IcoLeaf,
  'pe-boto':     IcoAward,
};

function ProgramCard({ prog, isPt, isOpen, onToggle }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    el.classList.add('appear');
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('show'); obs.unobserve(el); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const Icon = progIcons[prog.id] || IcoTarget;

  return (
    <div
      ref={ref}
      id={prog.id}
      className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
        isOpen
          ? 'border-brand-400 dark:border-brand-600 shadow-lg shadow-brand-600/10'
          : 'border-brand-100 dark:border-brand-900 bg-white dark:bg-brand-950 hover:border-brand-300 dark:hover:border-brand-800'
      }`}
    >
      {/* Orange top bar */}
      <div className={`h-0.5 transition-all duration-300 ${isOpen ? 'bg-brand-600' : 'bg-brand-100 dark:bg-brand-900'}`} />

      {/* Header — always visible */}
      <button
        onClick={onToggle}
        className="w-full text-left p-7 flex items-start gap-5 group"
      >
        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
          isOpen
            ? 'bg-brand-600 border-brand-600 text-white'
            : 'border-brand-100 dark:border-brand-900 text-brand-600 group-hover:bg-brand-50 dark:group-hover:bg-brand-900 group-hover:border-brand-300'
        }`}>
          <Icon cls="w-5 h-5" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold text-brand-500 uppercase tracking-[0.14em] mb-1.5">
                {prog.acronym}
              </div>
              <h3 className="text-[16px] font-bold text-brand-900 dark:text-white leading-snug">
                {isPt ? prog.title_pt : prog.title_en}
              </h3>
            </div>
            <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 transition-all duration-200 mt-0.5 ${
              isOpen
                ? 'bg-brand-600 border-brand-600 text-white'
                : 'border-brand-200 dark:border-brand-800 text-brand-500 group-hover:border-brand-400'
            }`}>
              <IcoChevronDown cls={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
          <p className="text-sm text-brand-600 dark:text-brand-500 mt-2 leading-relaxed">
            {isPt ? prog.subtitle_pt : prog.subtitle_en}
          </p>
        </div>
      </button>

      {/* Expanded content */}
      {isOpen && (
        <div className="px-7 pb-7 border-t border-brand-100 dark:border-brand-900">
          <div className="pt-6 grid sm:grid-cols-2 gap-8">
            {/* Description */}
            <div>
              <p className="body-md text-brand-700 dark:text-brand-400 leading-relaxed mb-6">
                {isPt ? prog.desc_pt : prog.desc_en}
              </p>

              {/* Actions */}
              <h4 className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.12em] mb-3">
                {isPt ? 'Acções Desenvolvidas' : 'Actions Undertaken'}
              </h4>
              <ul className="space-y-2">
                {(isPt ? prog.actions_pt : prog.actions_en).map((a, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-brand-700 dark:text-brand-400">
                    <div className="w-4 h-4 rounded bg-brand-100 dark:bg-brand-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-600 block" />
                    </div>
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas + photo */}
            <div>
              <h4 className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.12em] mb-3">
                {isPt ? 'Áreas de Actuação' : 'Areas of Action'}
              </h4>
              <div className="flex flex-wrap gap-2 mb-5">
                {(isPt ? prog.areas_pt : prog.areas_en).map((a, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-semibold px-3 py-1 rounded-full border border-brand-200 dark:border-brand-800 text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/40"
                  >
                    {a}
                  </span>
                ))}
              </div>

              {prog.province_pt && (
                <div className="mb-5">
                  <h4 className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.12em] mb-2">
                    {isPt ? 'Localização' : 'Location'}
                  </h4>
                  <p className="text-sm font-medium text-brand-700 dark:text-brand-400">
                    {isPt ? prog.province_pt : prog.province_en}
                  </p>
                </div>
              )}

              {(() => {
                const progPhotos = {
                  pdic:          'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9067.JPG',
                  tata:          'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/01.jpg',
                  geracao:       'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/f.jpg',
                  'seja-mulher': 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/ff.jpg.jpeg',
                  dikota:        'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/24.jpg',
                  campus:        'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/OIF.jpg',
                  revieng:       'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/DD.jpg',
                  'pe-boto':     'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/D%20(2).jpg',
                };
                return (
                  <Img
                    src={progPhotos[prog.id] || null}
                    alt={isPt ? prog.title_pt : prog.title_en}
                    className="w-full h-40 rounded-xl"
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProgrammesPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  const [openId, setOpenId] = useState(null);
  const ref = useAppear();

  const toggle = (id) => setOpenId((o) => (o === id ? null : id));

  return (
    <main>
      {/* Hero */}
      <section className="relative bg-brand-950 pt-[68px] overflow-hidden">
        <div className="absolute right-0 top-0 w-[600px] h-[600px] rounded-full bg-brand-600/8 blur-[120px] translate-x-1/2 -translate-y-1/3" />
        <div className="wrap relative z-10 py-24 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-800 bg-brand-900/60 mb-7">
            <span className="text-[11px] font-bold text-brand-500 uppercase tracking-[0.14em]">
              {isPt ? '8 Programas Activos' : '8 Active Programmes'}
            </span>
          </div>
          <h1 className="display-xl text-white mb-6">
            {isPt ? 'Programas e Projectos' : 'Programmes & Projects'}
          </h1>
          <p className="body-lg text-brand-400 max-w-2xl mx-auto">
            {isPt
              ? 'Oito programas estruturantes que cobrem as necessidades fundamentais das comunidades angolanas. Seleccione cada programa para saber mais.'
              : 'Eight key programmes covering the fundamental needs of Angolan communities. Select each programme to learn more.'}
          </p>
        </div>
      </section>

      {/* Programmes list */}
      <section className="section-pad bg-brand-50 dark:bg-brand-900/10">
        <div className="wrap">
          <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {programmes.map((prog) => (
              <ProgramCard
                key={prog.id}
                prog={prog}
                isPt={isPt}
                isOpen={openId === prog.id}
                onToggle={() => toggle(prog.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PDIC Feature */}
      <section className="section-pad bg-white dark:bg-brand-950">
        <div className="wrap">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-1.5 rounded-full bg-brand-50 dark:bg-brand-900/40 border border-brand-200 dark:border-brand-800 mb-5">
                <span className="text-[11px] font-bold text-brand-600 uppercase tracking-widest">
                  {isPt ? 'Projecto Piloto em Destaque' : 'Featured Pilot Project'}
                </span>
              </div>
              <h2 className="display-md text-brand-900 dark:text-white mb-4">
                {isPt ? 'PDIC — Comunidade do Toco, Huíla' : 'PDIC — Toco Community, Huíla'}
              </h2>
              <p className="body-md text-brand-600 dark:text-brand-500 max-w-2xl mx-auto">
                {isPt
                  ? 'O Programa de Desenvolvimento Integrado Comunitário representa um modelo inovador de desenvolvimento participativo, onde a própria população é envolvida activamente na transformação da sua realidade social.'
                  : 'The Integrated Community Development Programme represents an innovative model of participatory development, where the population itself is actively involved in transforming its social reality.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mb-8">
              {[
                { pt:'Infra-estruturas',    en:'Infrastructure',    d_pt:'Construção e reabilitação de equipamentos sociais.',      d_en:'Construction and rehabilitation of social facilities.'       },
                { pt:'Água Potável',        en:'Clean Water',       d_pt:'Sistemas de abastecimento de água nas comunidades.',       d_en:'Water supply systems in communities.'                        },
                { pt:'Agricultura',         en:'Agriculture',       d_pt:'Distribuição de sementes e árvores frutíferas.',           d_en:'Distribution of seeds and fruit trees.'                      },
                { pt:'Feiras de Saúde',     en:'Health Fairs',      d_pt:'Assistência médica itinerante e rastreios.',               d_en:'Itinerant medical assistance and screenings.'                },
                { pt:'Educação',            en:'Education',         d_pt:'Formação comunitária e técnico-profissional.',             d_en:'Community and technical-vocational training.'                },
                { pt:'Saneamento',          en:'Sanitation',        d_pt:'Campanhas de higiene, saneamento e ambiente.',            d_en:'Hygiene, sanitation and environmental campaigns.'           },
              ].map((item, i) => (
                <div key={i} className="card-base p-5">
                  <div className="h-0.5 w-6 bg-brand-600 mb-4" />
                  <h4 className="font-bold text-brand-900 dark:text-white text-sm mb-1.5">{isPt ? item.pt : item.en}</h4>
                  <p className="text-xs text-brand-600 dark:text-brand-500 leading-relaxed">{isPt ? item.d_pt : item.d_en}</p>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9047.JPG" alt="PDIC Toco 1" className="w-full h-52 rounded-2xl" />
              <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9090.JPG" alt="PDIC Toco 2" className="w-full h-52 rounded-2xl" />
              <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9449_1.JPG" alt="PDIC Toco 3" className="w-full h-52 rounded-2xl" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
